/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { inject, Injectable } from '@angular/core';
import { LoginService } from '@irian-ro/auth-shared-module';
import {
  getAllCompanyListRights,
  getCRMRights,
  getEmailAuthRights,
  getSAFTRights,
  getSelectItemsRights,
  Rights
} from '@well/rights';
import { Router } from '@angular/router';
import { map, switchMap } from 'rxjs/operators';
import { ContextService } from './context.service';
import { translationSchema } from '@well/translation';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { WellMenuItem } from './well-menu-item.model';
import { NavigationService } from '@well/navigation';
import { LoggedInContext } from '@well/shared-providers';

@Injectable({
  providedIn: 'root',
})
export class MainMenuItemsService {
  private readonly contextService = inject(ContextService);

  private whitelistAuthType = ['authPasswordRecovery', 'authEmailConfirmation'];

  labels = {
    crmLabel: translationSchema['shell-app']['navigationMenu']['crm'],
    deliveryLabel: translationSchema['shell-app']['navigationMenu']['delivery'],
    myCompanyLabel: translationSchema['shell-app']['navigationMenu']['myCompany'],
    selectItemsLabel: translationSchema['shell-app']['navigationMenu']['selectItems'],
    allCompaniesLabel: translationSchema['shell-app']['navigationMenu']['allCompanies'],
    logoutLabel: translationSchema['shell-app']['navigationMenu']['logout'],
    saftLabel: translationSchema['shell-app']['navigationMenu']['saftLabel'],
  };

  constructor(
    private loginService: LoginService,
    private translateService: TranslateService,
    private router: Router,
    private navigationService: NavigationService
  ) {}

  getMenu() {
    return this.contextService.onUserChange()?.pipe(
      switchMap((context) => {
        if (context && context.loggedInUser && context.loggedInUser.name) {
          return this.translateService.stream(Object.values(translationSchema['shell-app']['navigationMenu'])).pipe(
            map((res) => {
              const menuItems: WellMenuItem[] = [];

              const hasSignupRole = context.loggedInUser?.authorities?.includes(Rights.ROLE_SIGNUP);

              if (hasSignupRole) {
                menuItems.push({
                  id: 'logoutToolbarButton',
                  label: res[this.labels.logoutLabel],
                  command: () =>
                    this.router
                      .navigate([{ outlets: { 'popup-full': null } }])
                      .then(() => this.loginService.logout().subscribe()),
                });
              } else {
                menuItems.push({
                  id: 'crmToolbarButton',
                  label: res[this.labels.crmLabel],
                  icon: 'ui-icon-pie-chart',
                  command: () => this.redirect('crm'),
                  rights: getCRMRights(),
                });

                if (context.loggedInUser?.authType === 'authEmail') {
                  menuItems.push({
                    id: 'deliveryToolbarButton',
                    label: res[this.labels.deliveryLabel],
                    icon: 'ui-icon-near-me',
                    command: () => this.navigateByRights(context),
                    rights: getEmailAuthRights(),
                  });
                } else {
                  menuItems.push({
                    id: 'deliveryToolbarButton',
                    label: res[this.labels.deliveryLabel],
                    icon: 'ui-icon-near-me',
                    command: () => this.navigateByRights(context),
                  });
                }

                menuItems.push({
                  id: 'myCompanyToolbarButton',
                  label: res[this.labels.myCompanyLabel],
                  icon: 'ui-icon-business',
                  command: () => this.redirect('company'),
                });
                menuItems.push({
                  id: 'allCompaniesToolbarButton',
                  label: res[this.labels.allCompaniesLabel],
                  icon: 'ui-icon-device-hub',
                  command: () => this.redirect('company/public/list'),
                  rights: [getAllCompanyListRights(), getSelectItemsRights()],
                });
                menuItems.push({
                  id: 'selectItemsToolbarButton',
                  label: res[this.labels.selectItemsLabel],
                  command: () => this.redirect('crm/select-items'),
                  rights: getSelectItemsRights(),
                });

                menuItems.push({
                  id: 'saftToolbarButton',
                  label: res[this.labels.saftLabel],
                  command: () => this.redirect('saft'),
                  rights: getSAFTRights(),
                });

                if (
                  !this.whitelistAuthType.find(
                    (whitelistedAuthType) => whitelistedAuthType === context.loggedInUser?.authType
                  )
                ) {
                  menuItems.push({
                    id: 'logoutToolbarButton',
                    label: res[this.labels.logoutLabel],
                    command: () =>
                      this.router
                        .navigate([{ outlets: { 'popup-full': null } }])
                        .then(() => this.loginService.logout().subscribe()),
                  });
                }
              }

              return menuItems;
            })
          );
        }

        return of([]);
      })
    );
  }

  redirect(path: string) {
    this.router.navigate([{ outlets: { 'popup-full': null } }]).then(() => this.router.navigate([`/${path}`]));
  }

  navigateByRights(context: LoggedInContext) {
    this.router
      .navigate([{ outlets: { 'popup-full': null } }])
      .then(() => this.navigationService.navigateByRights(context));
  }
}
